import type { ActionsUnion } from '@rxsv/core';
import { U, createReducer, fromActions } from '@rxsv/core';
import { forkJoin, of } from 'rxjs';
import {
    switchMap,
    map,
    catchError,
    withLatestFrom,
    delayWhen,
    defaultIfEmpty,
} from 'rxjs/operators';
import { nullableToArray } from '@bayer-int/portletce-shared';

import { AsyncData } from 'portlet/common/models/asyncData';
import type { AppError } from 'portlet/common/errors';
import type { View, AcPipeDocument, AcSpecification, ViewId } from 'portlet/models';

import type { AppSelector } from '../selectors';
import type { EffectFactory } from '../effects';

export type ViewData = {
    viewId: ViewId;
    source: AcSpecification[];
    document: AcPipeDocument;
    transforms: AcSpecification[];
    utilities: AcSpecification[];
};

const initialState = {
    currentViewData: AsyncData.create<ViewData, AppError>(),
    isSuspended: false,
    cacheInvalidationTimestamp: null as string | null,
};

export const Action = U.createUnion(
    U.caseOf('RESET_VIEW')(),
    U.caseOf('RENDER_VIEW')<View>(),
    U.caseOf('VIEW_DATA_LOADED')<ViewData>(),
    U.caseOf('VIEW_DATA_ERROR')<AppError>(),
    U.caseOf('RELOAD_DATA')<string>(),
    U.caseOf('DOWNLOAD_DATA')(),
    U.caseOf('SUSPEND')(),
    U.caseOf('RESUME')(),
);
export const ErrorActions = [Action.VIEW_DATA_ERROR];

export type Action = ActionsUnion<typeof Action>;
export type State = typeof initialState;
export type Selector<T> = (state: State) => T;

export const reducer = createReducer(initialState)<Action>({
    RESET_VIEW: () => initialState,
    RENDER_VIEW: state => ({
        ...state,
        currentViewData:
            AsyncData.is.LOADED(state.currentViewData) ||
            AsyncData.is.RELOADING(state.currentViewData)
                ? AsyncData.reload(state.currentViewData.data)
                : AsyncData.request(),
    }),
    VIEW_DATA_LOADED: (state, { payload }) => ({
        ...state,
        currentViewData: AsyncData.load(payload),
    }),
    VIEW_DATA_ERROR: (state, { payload }) => ({
        ...state,
        currentViewData: AsyncData.error(payload),
    }),
    RELOAD_DATA: (state, { payload }) => ({
        ...state,
        cacheInvalidationTimestamp: payload,
    }),
    SUSPEND: state => ({ ...state, isSuspended: true }),
    RESUME: state => ({ ...state, isSuspended: false }),
    DOWNLOAD_DATA: state => state,
});

export const effect: EffectFactory =
    ({ vegaSpecService }) =>
    (action$, state$) => {
        const resume$ = action$.pipe(fromActions(Action.RESUME));

        const getView = action$.pipe(
            fromActions(Action.RENDER_VIEW),
            map(({ payload }) => payload),
            withLatestFrom(state$),
            switchMap(
                ([
                    props,
                    {
                        view: { isSuspended },
                    },
                ]) => (isSuspended ? of(props).pipe(delayWhen(() => resume$)) : of(props)),
            ),
            switchMap(({ id, datapipe, utilities }) => {
                const source$ = forkJoin(
                    nullableToArray(datapipe.source).map(spec => vegaSpecService.getVegaSpec(spec)),
                ).pipe(defaultIfEmpty([]));
                const document$ = vegaSpecService.getSpecDocument(datapipe.document);
                const transforms$ = datapipe.transforms.length
                    ? forkJoin(datapipe.transforms.map(spec => vegaSpecService.getVegaSpec(spec)))
                    : of([]);
                const utilities$ = utilities?.length
                    ? forkJoin(utilities.map(spec => vegaSpecService.getUtility(spec)))
                    : of([]);

                return forkJoin({
                    viewId: of(id),
                    source: source$,
                    document: document$,
                    transforms: transforms$,
                    utilities: utilities$,
                }).pipe(
                    map(Action.VIEW_DATA_LOADED),
                    catchError(err => of(Action.VIEW_DATA_ERROR(err))),
                );
            }),
        );

        return getView;
    };

const viewDataSelector: AppSelector<ViewData | null> = ({ view }) =>
    AsyncData.match(view.currentViewData, {
        LOADED: data => data,
        RELOADING: data => data,
    });

const cacheInvalidationTimestamp: AppSelector<string | null> = state =>
    state.view.cacheInvalidationTimestamp;

const isSuspendedSelector: AppSelector<boolean> = ({ view }) => view.isSuspended;

export const Selectors = {
    viewDataSelector,
    cacheInvalidationTimestamp,
    isSuspendedSelector,
};
